import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../services/supabase";
import { siteConfig } from "../../config/siteConfig";

const BillingPage = () => {
  const { profile, user, refreshProfile } = useAuth();
  const [showIframe, setShowIframe] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isCanceling, setIsCanceling] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isPremium = profile?.is_premium || false;
  const [isSuccess, setIsSuccess] = useState(false);

  const CHECKOUT_URL = siteConfig.payment.checkoutUrl;
  const CANCEL_ENDPOINT = siteConfig.payment.cancelSubscriptionEndpoint;
  const PROVIDER_NAME = siteConfig.payment.providerName;
  const plan = siteConfig.pricing.plan;

  const handleCancelSubscription = async () => {
    setIsCanceling(true);
    setErrorMessage("");

    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(CANCEL_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token || ""}`,
        },
        body: JSON.stringify({ email: user.email, reason: cancelReason }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "No pudimos procesar tu solicitud.");

      setIsSuccess(true);
      setTimeout(async () => {
        setShowCancelModal(false);
        setIsSuccess(false);
        await refreshProfile();
      }, 2000);
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setIsCanceling(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("schema-db-changes")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "profiles",
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          refreshProfile();
          if (payload.new.is_premium === true) {
            setShowIframe(false);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const handleCloseIframe = async () => {
    setShowIframe(false);
    if (user?.id) {
      await refreshProfile();
    }
  };

  const handleStartProcess = () => {
    setShowWarning(true);
  };

  const handleConfirmAndPay = (targetUrl) => {
    setShowWarning(false);
    // Stripe Checkout n'autorise pas l'iframe : on ouvre un onglet dédié,
    // en attachant l'utilisateur pour que le webhook active le bon compte.
    const sep = targetUrl.includes("?") ? "&" : "?";
    const finalUrl = `${targetUrl}${sep}client_reference_id=${user?.id || ""}&prefilled_email=${encodeURIComponent(user?.email || "")}`;
    window.open(finalUrl, "_blank", "noopener");
    setShowIframe(true);
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 animate-fadeIn relative">
      <div className="mb-10">
        <h1 className="text-3xl font-black text-slate-800">Suscripción</h1>
        <p className="text-slate-500">Gestiona tu acceso al simulador premium</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6">Tu Plan Actual</h2>
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl mb-8">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl ${isPremium ? "bg-amber-100" : "bg-blue-100"}`}>
              {isPremium ? "💎" : "✨"}
            </div>
            <div>
              <p className="text-xl font-black text-slate-800">{isPremium ? "Membresía PRO" : "Plan Gratuito"}</p>
              <p className="text-sm text-slate-500">{isPremium ? "Acceso Total Desbloqueado" : "Acceso Limitado"}</p>
            </div>
            {isPremium && (
              <div className="text-right">
                <p className="text-xs font-bold text-slate-400 uppercase">
                  {plan.currency}{plan.price}{plan.period}
                </p>
              </div>
            )}
          </div>
          {isPremium && (
            <div className="mt-6 pt-6 border-t border-slate-100 flex justify-center">
              <button
                onClick={() => setShowCancelModal(true)}
                className="text-xs font-bold text-slate-400 hover:text-red-500 transition-colors uppercase tracking-widest underline decoration-transparent hover:decoration-red-500 underline-offset-4"
              >
                ¿Deseas cancelar tu suscripción?
              </button>
            </div>
          )}
        </div>

        {!isPremium && (
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-8 rounded-3xl text-white shadow-xl shadow-blue-200">
            <h3 className="text-2xl font-black mb-4">¿Listo para aprobar?</h3>
            <p className="text-blue-100 mb-8 font-medium">Desbloquea todo con el Plan Premium.</p>
            <ul className="space-y-4 mb-10">
              {plan.benefitsList.map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-sm font-bold">
                  <svg className="w-5 h-5 text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                  </svg>
                  {b}
                </li>
              ))}
            </ul>
            <button
              onClick={handleStartProcess}
              className="w-full py-4 bg-white text-blue-600 rounded-xl font-black text-lg hover:bg-blue-50 transition-all shadow-lg active:scale-95"
            >
              SUSCRIBIRME POR {plan.currency}{plan.price} ⚡
            </button>
          </div>
        )}
      </div>

      {showWarning && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-md animate-fadeIn" />
          <div className="relative bg-white w-full max-w-md rounded-3xl p-8 shadow-2xl animate-scaleIn text-center">
            <div className="w-20 h-20 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-4xl mx-auto mb-6">⚠️</div>
            <h2 className="text-2xl font-black text-slate-800 mb-4">Paso Importante</h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Para activar tu cuenta al instante, asegúrate de escribir este correo en el formulario de pago:
            </p>
            <div className="bg-slate-100 p-4 rounded-2xl mb-8 border-2 border-dashed border-slate-300">
              <span className="text-lg font-black text-blue-600 break-all">{user?.email}</span>
            </div>
            <div className="flex flex-col gap-3">
              <button onClick={() => handleConfirmAndPay(CHECKOUT_URL)} className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-700 transition-all">
                ENTENDIDO, IR A PAGAR
              </button>
              <button onClick={() => setShowWarning(false)} className="w-full py-3 text-slate-400 font-bold hover:text-slate-600 transition-all">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showIframe && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-10">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleCloseIframe} />
          <div className="relative bg-white w-full max-w-4xl h-full max-h-[90vh] rounded-3xl overflow-hidden shadow-2xl flex flex-col animate-scaleIn">
            <div className="bg-white p-4 flex justify-between items-center border-b">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full animate-pulse ${isPremium ? "bg-blue-500" : "bg-green-500"}`} />
                <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest">
                  {isPremium ? "Portal de Gestión" : `Pago Seguro con ${PROVIDER_NAME}`}
                </span>
              </div>
              <button onClick={handleCloseIframe} className="text-slate-400 hover:text-red-500 p-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-grow relative bg-slate-50 flex flex-col items-center justify-center text-center p-8">
              <div className="w-10 h-10 border-4 border-slate-200 rounded-full animate-spin border-t-blue-600 mb-6" />
              <h3 className="text-xl font-black text-slate-800 mb-2">Completa el pago en la pestaña que se abrió</h3>
              <p className="text-sm text-slate-500 max-w-sm mb-8">
                Esta página se actualizará automáticamente en cuanto confirmemos tu pago. No cierres esta ventana.
              </p>
              <button onClick={handleCloseIframe} className="text-xs font-bold text-slate-400 hover:text-blue-600 uppercase tracking-widest underline underline-offset-4">
                Ya pagué, actualizar mi cuenta
              </button>
            </div>
          </div>
        </div>
      )}
      {showCancelModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => !isCanceling && setShowCancelModal(false)} />
          <div className="relative bg-white w-full max-w-sm rounded-[2rem] p-8 shadow-2xl animate-scaleIn border border-slate-100">
            {isSuccess ? (
              <div className="text-center py-8 animate-fadeIn">
                <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">✓</div>
                <h3 className="font-black text-slate-800 text-lg mb-2">Suscripción cancelada</h3>
                <p className="text-sm text-slate-500">Mantienes tu acceso premium hasta el final del período ya pagado. No se realizarán más cobros.</p>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-black text-slate-800 mb-2">¿Quieres cancelar?</h2>
                <p className="text-sm text-slate-500 mb-6">Lamentamos que te vayas. ¿Nos cuentas por qué?</p>
                <select
                  className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 mb-6 focus:ring-2 focus:ring-red-500 outline-none transition-all font-medium"
                  onChange={(e) => setCancelReason(e.target.value)}
                  disabled={isCanceling}
                >
                  <option value="">Selecciona una razón...</option>
                  <option value="precio">El precio es muy elevado</option>
                  <option value="uso">Ya no utilizo la plataforma</option>
                  <option value="tecnico">Problemas técnicos</option>
                  <option value="otro">Otro motivo</option>
                </select>
                <div className="mb-6 p-4 bg-amber-50 border border-amber-100 rounded-2xl flex gap-3">
                  <span className="text-xl">⚠️</span>
                  <p className="text-xs font-bold text-amber-800 leading-relaxed">
                    Al confirmar, tu suscripción no se renovará. Mantienes tu acceso premium hasta el final del período ya pagado.
                  </p>
                </div>
                {errorMessage && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3">
                    <span className="text-lg">❌</span>
                    <p className="text-xs font-bold text-red-600 leading-tight">{errorMessage}</p>
                  </div>
                )}
                <div className="flex flex-col gap-3">
                  <button disabled={!cancelReason || isCanceling} onClick={handleCancelSubscription} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black hover:bg-slate-800 transition-all disabled:opacity-50 shadow-lg">
                    {isCanceling ? "Procesando..." : "Confirmar Cancelación"}
                  </button>
                  <button onClick={() => setShowCancelModal(false)} className="w-full py-3 font-bold text-slate-400 hover:text-slate-600 transition-all" disabled={isCanceling}>
                    Mantener mi suscripción
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
